import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../../shared/ui/kit/utils';
import type { Feature } from '../types/about.types';

interface FeatureCardProps {
    feature: Feature;
    index: number;
    onClick?: () => void;
}

const colorMap = {
    blue: {
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        icon: 'text-blue-600 dark:text-blue-400',
        border: 'hover:border-blue-200 dark:hover:border-blue-500/30',
        gradient: 'from-blue-500 to-blue-600',
    },
    emerald: {
        bg: 'bg-emerald-50 dark:bg-emerald-900/20',
        icon: 'text-emerald-600 dark:text-emerald-400',
        border: 'hover:border-emerald-200 dark:hover:border-emerald-500/30',
        gradient: 'from-emerald-500 to-emerald-600',
    },
    amber: {
        bg: 'bg-amber-50 dark:bg-amber-900/20',
        icon: 'text-amber-600 dark:text-amber-400',
        border: 'hover:border-amber-200 dark:hover:border-amber-500/30',
        gradient: 'from-amber-500 to-amber-600',
    },
    purple: {
        bg: 'bg-purple-50 dark:bg-purple-900/20',
        icon: 'text-purple-600 dark:text-purple-400',
        border: 'hover:border-purple-200 dark:hover:border-purple-500/30',
        gradient: 'from-purple-500 to-purple-600',
    },
    rose: {
        bg: 'bg-rose-50 dark:bg-rose-900/20',
        icon: 'text-rose-600 dark:text-rose-400',
        border: 'hover:border-rose-200 dark:hover:border-rose-500/30',
        gradient: 'from-rose-500 to-rose-600',
    },
    cyan: {
        bg: 'bg-cyan-50 dark:bg-cyan-900/20',
        icon: 'text-cyan-600 dark:text-cyan-400',
        border: 'hover:border-cyan-200 dark:hover:border-cyan-500/30',
        gradient: 'from-cyan-500 to-cyan-600',
    },
};

const IconComponents: Record<string, React.FC<{ className?: string }>> = {
    Shield: ({ className }) => (
        <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
    ),
    Zap: ({ className }) => (
        <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
        </svg>
    ),
    Smile: ({ className }) => (
        <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10"/>
            <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
            <line x1="9" y1="9" x2="9.01" y2="9"/>
            <line x1="15" y1="9" x2="15.01" y2="9"/>
        </svg>
    ),
    Smartphone: ({ className }) => (
        <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <rect x="5" y="2" width="14" height="20" rx="2"/>
            <line x1="12" y1="18" x2="12.01" y2="18"/>
        </svg>
    ),
    Wallet: ({ className }) => (
        <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"/>
            <path d="M20 12v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8h16z"/>
        </svg>
    ),
    Lock: ({ className }) => (
        <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
    ),
};

export const FeatureCard: React.FC<FeatureCardProps> = ({ 
    feature, 
    index,
    onClick 
}) => {
    const colors = colorMap[feature.color];
    const Icon = IconComponents[feature.icon];

    return (
        <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ 
                duration: 0.5, 
                delay: index * 0.1,
                ease: [0.25, 0.46, 0.45, 0.94]
            }}
            whileHover={{ 
                y: -4,
                transition: { duration: 0.2 }
            }}
            whileTap={{ scale: 0.98 }}
            onClick={onClick}
            className={cn(
                'feature-card group relative p-5 rounded-xl',
                'bg-white dark:bg-white/5',
                'border border-black/[0.07] dark:border-white/[0.07]',
                'transition-all duration-300 cursor-pointer',
                'hover:shadow-lg hover:shadow-black/5 dark:hover:shadow-black/20',
                colors.border
            )}
        >
            <div className={cn(
                'absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500',
                'bg-gradient-to-br',
                colors.gradient,
                'opacity-0 group-hover:opacity-[0.03] dark:group-hover:opacity-[0.05]'
            )} />

            <div className={cn(
                'w-11 h-11 rounded-xl flex items-center justify-center mb-4',
                'transition-transform duration-300 group-hover:scale-110',
                colors.bg
            )}>
                <Icon className={cn('w-5 h-5', colors.icon)} />
            </div>

            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                {feature.title}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                {feature.description}
            </p>

            <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden rounded-tr-xl pointer-events-none">
                <div className={cn(
                    'absolute top-0 right-0 w-16 h-16 translate-x-8 -translate-y-8 rotate-45',
                    'bg-gradient-to-br opacity-0 group-hover:opacity-10 transition-opacity duration-300',
                    colors.gradient
                )} />
            </div>
        </motion.div>
    );
};